import { useCallback, useMemo, useState } from 'react';

import type { DeleteProductsModalProps } from '@app//components/Modals/DeleteProductsModal/ui/DeleteProductsModal.main';
import {
  useDeleteProductsMutation,
  useGetProductByIdQuery,
} from '@app/api/Products';
import type {
  ModelProductsDeleting,
  ModelProductsDeletingRequest,
} from '@app/api/Products';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { useErrorHandler } from '@app/shared/hooks';

export const useDeleteProductsModal = ({
  onClose,
  afterDelete,
  ProductId,
  WarehouseId,
  PriceOfSaleS,
}: DeleteProductsModalProps) => {
  const {
    params: { ActiveBranchId },
  } = useUserPrint();

  const { showNotification } = useNotification();

  const [countToDelete, setCountToDelete] = useState<number | null>(null);

  const [deleteProducts, { isLoading: isDeleting }] =
    useDeleteProductsMutation();

  const {
    data: product,
    error: productError,
    isError: isProductError,
    isFetching: isProductFetching,
    isLoading: isProductLoading,
    isUninitialized: isProductUninitialized,
  } = useGetProductByIdQuery(
    { ActiveBranchId, productId: ProductId },
    {
      skip: !ProductId || !ActiveBranchId,
      refetchOnMountOrArgChange: true,
    }
  );

  useErrorHandler([{ flag: isProductError, error: productError }]);

  const deleteHandler = useCallback(async () => {
    if (!countToDelete) {
      return;
    }

    if (!product) {
      return;
    }

    const { SupplierId } = product;

    const payload: ModelProductsDeleting = {
      DateDeletion: new Date().toISOString(),
      WarehouseId,
      Products: [
        {
          ProductId,
          Count: countToDelete,
          PriceOfSale: PriceOfSaleS,
          SupplierId,
        },
      ],
      Comment: null,
    };

    const request: ModelProductsDeletingRequest = {
      ActiveBranchId,
      data: payload,
    };

    try {
      await deleteProducts(request).unwrap();
      showNotification('Товар успешно удалён со склада', 'success');
      afterDelete?.();
      onClose();
    } catch (error) {
      showNotification(error);
    }
  }, [
    ActiveBranchId,
    WarehouseId,
    ProductId,
    PriceOfSaleS,
    countToDelete,
    deleteProducts,
    showNotification,
    onClose,
    afterDelete,
    product,
  ]);

  const isLoading = useMemo(
    () => isProductFetching || isProductLoading || isProductUninitialized,
    [isProductFetching, isProductLoading, isProductUninitialized]
  );

  return {
    countToDelete,
    setCountToDelete,
    isDeleting,
    deleteHandler,
    isLoading,
  };
};
